import {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {
  RunSelectorRunsQueryVariables,
  useRunSelectorRunsQuery,
} from '../../../generated/graphql';
import {useSelector} from '../../../state/hooks';
import {useSyncedPollingSubscriber} from '../../../state/polling/hooks';
import * as RunSelectorRunActions from '../../../state/runSelectorRuns/actions';
import {
  EMPTY_GROUPED_DATA,
  ROOT_GROUP_ID,
} from '../../../state/runSelectorRuns/reducer';
import {propagateErrorsContext} from '../../../util/errors';
import * as Filters from '../../../util/filters';
import {isNetworkRequestSettled} from '../../../util/isNetworkRequestSettled';
import {perfTimerFromPageLoad} from '../../../util/profiler';
import * as Query from '../../../util/queryts';
import {useRampFlagRunSelectorRunsQuery} from '../../../util/rampFeatureFlags';
import * as Run from '../../../util/runs';
import {useRunsLowData} from './useRunsLowData';
import {useRunsPaging} from './useRunsPaging';

type DeltaVariables = Pick<
  RunSelectorRunsQueryVariables,
  'currentRuns' | 'lastUpdated'
>;

export type UseRunSelectorRunsArgs = {
  /** If true, will not query for runs */
  disabled?: boolean;
  /**
   * Unique ID for the grouped runs data managed by this instance of useRunSelectorRuns.
   * The top-level results, while not technically "grouped", are also treated as a group
   * using a special ROOT_GROUP_ID.
   */
  groupId?: string;
  /** Determines how many runs will be returned on initial load and when loading more runs */
  pageSize: number;
  /**
   * Variables that describe the state of RunSelector at the top level.
   * These serve as the baseline input for the RunSelectorRuns query.
   */
  topLevelQueryVariables: Query.Query;
  /**
   * Each consumer of this hook inside RunSelector has slightly different needs
   * (e.g. CSV export, expanded groups). These `overrideOptions` allow consumers
   * to specify how the base topLevelQueryVariables should be modified for its needs
   */
  overrideOptions?: {
    /** If true, filters will be stricter so only selected (visualized) runs are returned */
    shouldOnlyShowSelected?: boolean;
  };
};

type RunSelectorRunsResult = {
  /**
   * Indicates whether we're loading runs. Could be from explicit user
   * interactions (e.g. searching, paging, ...) or automatic polling.
   */
  isLoadingRuns: boolean;
  /**
   * Indicates whether we're loading for a polled request.
   * Allows more granular control for loading UI.
   * @todo update type to be required once off of runs-low
   */
  isLoadingRunsPolled?: boolean;
  /**
   * Function for paging. When navigating between pages,
   * call this with the new page number to ensure runs for
   * that page will be loaded (as necessary; may be cached)
   * @todo update type to be required once off of runs-low
   */
  loadMoreRuns?: (page: number) => void;
  /**
   * Detailed runs data. Only contains enough data to
   * satisfy the current paging criteria
   */
  runs: Run.Run[];
  /**
   * Total number of runs that would match the query,
   * regardless of the paging limit
   */
  totalRuns: number;
};

export const useRunSelectorRuns = (
  args: UseRunSelectorRunsArgs
): RunSelectorRunsResult => {
  const isRunSelectorRunsQueryEnabled = useRampFlagRunSelectorRunsQuery();

  const {
    disabled = false,
    groupId = ROOT_GROUP_ID,
    pageSize,
    topLevelQueryVariables,
    overrideOptions = {},
  } = args;
  const {shouldOnlyShowSelected} = overrideOptions;

  // Track these values so the backend knows what to compute deltas against.
  // Must use ref because we don't want to trigger a new request every time
  // these values are updated; instead, we just want to make sure they're
  // correctly populated when a request is made.
  const deltaVariablesRef = useRef<DeltaVariables>({
    currentRuns: [],
    lastUpdated: new Date(0),
  });

  const {limit, loadMoreRuns} = useRunsPaging(pageSize);

  const queryVariables = useMemo(() => {
    const filters =
      shouldOnlyShowSelected && topLevelQueryVariables.selections != null
        ? Filters.And([
            topLevelQueryVariables.filters,
            topLevelQueryVariables.selections,
          ])
        : topLevelQueryVariables.filters;

    // We can move these directly into topLevelQueryVariables once fully off of runs-low
    const enableAggregations = !!topLevelQueryVariables.aggregationKeys?.length;
    const enableConfig = !!topLevelQueryVariables.configKeys?.length;
    const enableSummary = !!topLevelQueryVariables.summaryKeys?.length;
    const groupKeys =
      topLevelQueryVariables.grouping?.map(Run.keyToServerPath) ?? [];
    const groupLevel = 0;
    const order = Query.sortToOrderString(topLevelQueryVariables.sort);

    const variables: RunSelectorRunsQueryVariables = {
      ...deltaVariablesRef.current,
      ...topLevelQueryVariables,
      enableAggregations,
      enableConfig,
      enableSummary,
      filters: JSON.stringify(Filters.toMongo(filters)),
      groupKeys,
      groupLevel,
      limit,
      order,
    };
    return variables;
  }, [limit, shouldOnlyShowSelected, topLevelQueryVariables]);

  const skip = disabled || !isRunSelectorRunsQueryEnabled;
  const {data, refetch, networkStatus} = useRunSelectorRunsQuery({
    context: propagateErrorsContext(),
    fetchPolicy: 'no-cache',
    skip,
    variables: queryVariables,
  });
  const isLoadingRuns = !skip && !isNetworkRequestSettled(networkStatus);
  const isInitialLoaded = useRef(false); // for perf logging

  // Read run selector runs data from redux
  const {runs, totalRuns, lastUpdatedAt} = useSelector(state => {
    const groupData = state.runSelectorRuns[groupId];
    if (groupId === ROOT_GROUP_ID && groupData && !isInitialLoaded.current) {
      isInitialLoaded.current = true;
      perfTimerFromPageLoad({
        name: 'run_selector_initial_loaded',
        interestingThreshold: 0,
      });
    }
    return groupData ?? EMPTY_GROUPED_DATA;
  });

  // Write updates to redux store
  const dispatch = useDispatch();
  const queryResults = data?.project?.runs;
  useEffect(() => {
    if (queryResults) {
      dispatch(
        RunSelectorRunActions.processQueryResults(groupId, queryResults)
      );
    }
  }, [dispatch, groupId, queryResults]);

  // Remove redux state on unmount
  useEffect(() => {
    return () => {
      dispatch(RunSelectorRunActions.removeGroup(groupId));
    };
  }, []); // eslint-disable-line

  // Keep delta vars ref up to date
  useEffect(() => {
    deltaVariablesRef.current = {
      currentRuns: runs.map(run => run.id),
      lastUpdated: new Date(lastUpdatedAt),
    };
  }, [runs, lastUpdatedAt]);

  // Subscribe to synced polling so RunSelector updates in sync with Charts
  const {lastTickedAt, stopTaskTimer} =
    useSyncedPollingSubscriber('RunSelector');
  const [isPollTaskActive, setIsPollTaskActive] = useState(false);

  // Refetch on poll tick
  useEffect(() => {
    if (!skip && lastTickedAt != null && !isLoadingRuns) {
      setIsPollTaskActive(true);
      refetch({
        ...queryVariables,
        ...deltaVariablesRef.current,
      });
    }
  }, [lastTickedAt]); // eslint-disable-line react-hooks/exhaustive-deps -- should ONLY trigger on poll tick!

  // Record when polled requests are loaded
  useEffect(() => {
    if (!isLoadingRuns && isPollTaskActive) {
      setIsPollTaskActive(false);
      stopTaskTimer();
    }
  }, [isPollTaskActive, isLoadingRuns, stopTaskTimer]);

  const results = useMemo(
    () => ({
      isLoadingRuns,
      isLoadingRunsPolled: isPollTaskActive,
      loadMoreRuns,
      runs,
      totalRuns,
    }),
    [isLoadingRuns, isPollTaskActive, loadMoreRuns, runs, totalRuns]
  );

  // We can delete runs-low stuff once fully ramp onto RunSelectorRuns query
  const runsLowResults = useRunsLowData({
    ...args,
    disabled: disabled || isRunSelectorRunsQueryEnabled,
  });
  if (!isRunSelectorRunsQueryEnabled) {
    return runsLowResults;
  }

  return results;
};
